import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const CreditPolicyConfigPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);

      const response = await api.get('/api/credit-policy/config');
      const data = response.data;
      setPolicies(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar políticas:', error);
      toast.error('Erro ao carregar políticas');
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy.risk_level);
    setFormData({
      min_score: policy.min_score,
      max_score: policy.max_score,
      default_deposit_months: policy.default_deposit_months,
      default_requires_guarantor: policy.default_requires_guarantor,
      allow_override: policy.allow_override,
      override_requires_approval: policy.override_requires_approval,
      approval_role: policy.approval_role
    });
  };

  const handleSave = async (riskLevel) => {
    try {
      await api.put(`/api/credit-policy/config/${riskLevel}`, formData);

      toast.success('Política atualizada com sucesso!');
      setEditingPolicy(null);
      loadPolicies();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      const message = error.response?.data?.error || 'Erro ao salvar alterações';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const handleCancel = () => {
    setEditingPolicy(null);
    setFormData({});
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      'very_low': 'Muito Baixo',
      'low': 'Baixo',
      'medium': 'Médio',
      'high': 'Alto',
      'very_high': 'Muito Alto'
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'very_low': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      'low': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      'medium': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      'high': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      'very_high': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
    };
    return colors[level] || colors.medium;
  };

  const getApprovalRoleLabel = (role) => {
    const labels = {
      'manager': 'Gerente',
      'director': 'Diretor',
      'admin': 'Administrador'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600 dark:text-gray-400">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheckIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Configuração de Política de Crédito
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Configure as regras de garantias e aprovações baseadas no nível de risco de crédito
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Importante:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>As políticas definem garantias padrão baseadas no score de crédito do cliente</li>
                <li>Exceções podem ser solicitadas e requerem aprovação conforme o nível configurado</li>
                <li>Alterações afetam novos contratos e análises de crédito</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Policies Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nível de Risco
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Caução
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fiador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Flexível?
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Aprovador
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {policies.map((policy) => {
                  const isEditing = editingPolicy === policy.risk_level;

                  return (
                    <tr
                      key={policy.id}
                      className={`${isEditing ? 'bg-blue-50 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                    >
                      {/* Nível de Risco */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getRiskLevelColor(policy.risk_level)}`}>
                          {getRiskLevelLabel(policy.risk_level)}
                        </span>
                      </td>

                      {/* Score */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex gap-2 items-center">
                            <input
                              type="number"
                              value={formData.min_score}
                              onChange={(e) => setFormData({ ...formData, min_score: parseInt(e.target.value) })}
                              className="w-16 px-2 py-1 text-sm border rounded"
                              min="0"
                              max="100"
                            />
                            <span>-</span>
                            <input
                              type="number"
                              value={formData.max_score}
                              onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) })}
                              className="w-16 px-2 py-1 text-sm border rounded"
                              min="0"
                              max="100"
                            />
                          </div>
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-white font-mono">
                            {policy.min_score} - {policy.max_score}
                          </span>
                        )}
                      </td>

                      {/* Caução */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.5"
                              value={formData.default_deposit_months || ''}
                              onChange={(e) => setFormData({ ...formData, default_deposit_months: parseFloat(e.target.value) })}
                              className="w-20 px-2 py-1 text-sm border rounded"
                              min="0"
                              max="12"
                            />
                            <span className="text-xs text-gray-600 dark:text-gray-400">meses</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-white">
                            {policy.default_deposit_months ? `${policy.default_deposit_months} mês${policy.default_deposit_months > 1 ? 'es' : ''}` : 'N/A'}
                          </span>
                        )}
                      </td>

                      {/* Fiador */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={formData.default_requires_guarantor}
                            onChange={(e) => setFormData({ ...formData, default_requires_guarantor: parseInt(e.target.value) })}
                            className="px-2 py-1 text-sm border rounded"
                          >
                            <option value={0}>Não</option>
                            <option value={1}>Sim</option>
                          </select>
                        ) : (
                          policy.default_requires_guarantor ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-gray-400" />
                          )
                        )}
                      </td>

                      {/* Flexível */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <div className="space-y-1">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={formData.allow_override === 1}
                                onChange={(e) => setFormData({ ...formData, allow_override: e.target.checked ? 1 : 0 })}
                                className="rounded"
                              />
                              <span className="text-gray-700 dark:text-gray-300">Permitir</span>
                            </label>
                            {formData.allow_override === 1 && (
                              <label className="flex items-center gap-2 text-xs ml-6">
                                <input
                                  type="checkbox"
                                  checked={formData.override_requires_approval === 1}
                                  onChange={(e) => setFormData({ ...formData, override_requires_approval: e.target.checked ? 1 : 0 })}
                                  className="rounded"
                                />
                                <span className="text-gray-600 dark:text-gray-400">Req. aprovação</span>
                              </label>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm">
                            {policy.allow_override ? (
                              <span className="text-green-600 dark:text-green-400">
                                Sim {policy.override_requires_approval ? '(com aprovação)' : ''}
                              </span>
                            ) : (
                              <span className="text-gray-500">Não</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Aprovador */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={formData.approval_role}
                            onChange={(e) => setFormData({ ...formData, approval_role: e.target.value })}
                            className="px-2 py-1 text-sm border rounded"
                          >
                            <option value="manager">Gerente</option>
                            <option value="director">Diretor</option>
                            <option value="admin">Administrador</option>
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-white">
                            {getApprovalRoleLabel(policy.approval_role)}
                          </span>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isEditing ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleSave(policy.risk_level)}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            >
                              Salvar
                            </button>
                            <button
                              onClick={handleCancel}
                              className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(policy)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                          >
                            <PencilSquareIcon className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            Legenda:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <strong>Score:</strong> Faixa de pontuação de crédito (0-100)
            </div>
            <div>
              <strong>Caução:</strong> Quantidade de meses de aluguel como garantia
            </div>
            <div>
              <strong>Fiador:</strong> Exige fiador/avalista no contrato
            </div>
            <div>
              <strong>Flexível:</strong> Permite solicitar exceções às regras
            </div>
            <div>
              <strong>Aprovador:</strong> Nível mínimo para aprovar exceções
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPolicyConfigPage;
